import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Zap, LogOut, Loader2, Trash2, Upload, RefreshCw, CalendarOff, Star, QrCode } from "lucide-react";
import { toast } from "sonner";
import api, { resolveImage, formatApiError } from "@/api";
import { useAuth } from "@/auth/AuthContext";

const STATUS_STYLES = {
  pending: "bg-amber-500/20 text-amber-400 border-amber-500/40",
  confirmed: "bg-blue-500/20 text-blue-400 border-blue-500/40",
  completed: "bg-emerald-500/20 text-emerald-400 border-emerald-500/40",
  cancelled: "bg-rose-500/20 text-rose-400 border-rose-500/40",
};
const FILTERS = ["all", "pending", "confirmed", "completed", "cancelled"];

const BookingsPanel = () => {
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/bookings").then((r) => setBookings(r.data)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const setStatus = async (id, status) => {
    try {
      const r = await api.patch(`/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? r.data : b)));
      toast.success(`Marked ${status}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const counts = FILTERS.slice(1).reduce((acc, s) => ({ ...acc, [s]: bookings.filter((b) => b.status === s).length }), {});
  const visible = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  return (
    <div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="admin-stats">
        {FILTERS.slice(1).map((s) => (
          <div key={s} className="bg-[#0B0F17] border border-amber-500/15 p-4">
            <p className="text-3xl font-condensed font-extrabold text-slate-100">{counts[s]}</p>
            <p className="text-xs font-mono-brand uppercase tracking-[0.2em] text-slate-500">{s}</p>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-6">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`bookings-filter-${f}`}
                  className={`text-xs font-mono-brand uppercase tracking-[0.15em] px-4 py-2 border transition-colors ${filter === f ? "bg-amber-500 text-slate-950 border-amber-500" : "text-slate-400 border-slate-700 hover:border-amber-500/50"}`}>
            {f}
          </button>
        ))}
        <button onClick={load} data-testid="bookings-refresh-button" className="ml-auto text-slate-500 hover:text-amber-400 transition-colors" aria-label="Refresh">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>
      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
      ) : visible.length === 0 ? (
        <p className="text-slate-500 text-sm py-16 text-center" data-testid="bookings-empty">No bookings in this view yet.</p>
      ) : (
        <div className="space-y-4">
          {visible.map((b) => (
            <div key={b.id} data-testid={`booking-row-${b.id}`} className="bg-[#0B0F17] border border-amber-500/15 p-5 flex flex-col lg:flex-row lg:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-3 mb-1">
                  <span className="font-condensed font-bold uppercase tracking-wide text-slate-100">{b.service_name}</span>
                  <span className={`text-[10px] font-mono-brand uppercase tracking-[0.15em] border px-2 py-0.5 ${STATUS_STYLES[b.status]}`} data-testid={`booking-status-${b.id}`}>{b.status}</span>
                </div>
                <p className="text-sm text-slate-400">{b.name} — {b.phone} — {b.email}</p>
                <p className="text-sm text-slate-500">{b.date} at {b.time_slot} · {b.address}</p>
                {b.description && <p className="text-sm text-slate-500 mt-1 italic">“{b.description}”</p>}
                {b.photos && b.photos.length > 0 && (
                  <div className="flex gap-2 mt-3" data-testid={`booking-photos-${b.id}`}>
                    {b.photos.map((p, i) => (
                      <a key={i} href={resolveImage(p)} target="_blank" rel="noopener noreferrer" data-testid={`booking-photo-${b.id}-${i}`}>
                        <img src={resolveImage(p)} alt={`Job photo ${i + 1}`}
                             className="w-16 h-16 object-cover border border-amber-500/30 hover:border-amber-500 transition-colors" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                {[["confirmed", "Confirm"], ["completed", "Complete"], ["cancelled", "Cancel"]].map(([s, label]) => (
                  <button key={s} onClick={() => setStatus(b.id, s)} disabled={b.status === s} data-testid={`booking-${s}-button-${b.id}`}
                          className="text-xs font-mono-brand uppercase tracking-[0.1em] border border-slate-700 hover:border-amber-500/60 text-slate-300 hover:text-amber-400 px-3 py-2 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const GalleryPanel = () => {
  const [items, setItems] = useState([]);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/gallery").then((r) => setItems(r.data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const upload = async (e) => {
    e.preventDefault();
    if (!beforeFile || !afterFile) {
      toast.error("Choose both a before and an after photo");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", title);
      fd.append("category", category);
      fd.append("before", beforeFile);
      fd.append("after", afterFile);
      await api.post("/gallery", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Gallery item added");
      setTitle(""); setCategory(""); setBeforeFile(null); setAfterFile(null);
      e.target.reset();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/gallery/${id}`);
      setItems((items) => items.filter((i) => i.id !== id));
      toast.success("Removed");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <form onSubmit={upload} data-testid="gallery-upload-form" className="bg-[#0B0F17] border border-amber-500/15 p-6 mb-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <input required value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Project title (e.g. Driveway Deep Clean)" data-testid="gallery-title-input"
               className="bg-[#0F172A] border border-slate-700 focus:border-amber-500 outline-none px-4 py-3 text-sm text-slate-100" />
        <input required value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category (e.g. Pressure Washing)" data-testid="gallery-category-input"
               className="bg-[#0F172A] border border-slate-700 focus:border-amber-500 outline-none px-4 py-3 text-sm text-slate-100" />
        <div>
          <label className="block text-xs font-mono-brand uppercase tracking-[0.2em] text-slate-500 mb-2">Before Photo</label>
          <input required type="file" accept="image/*" onChange={(e) => setBeforeFile(e.target.files[0])} data-testid="gallery-before-input"
                 className="text-xs text-slate-400 file:mr-3 file:bg-amber-500 file:text-slate-950 file:border-0 file:px-3 file:py-2 file:font-mono-brand file:text-xs file:cursor-pointer" />
        </div>
        <div>
          <label className="block text-xs font-mono-brand uppercase tracking-[0.2em] text-slate-500 mb-2">After Photo</label>
          <input required type="file" accept="image/*" onChange={(e) => setAfterFile(e.target.files[0])} data-testid="gallery-after-input"
                 className="text-xs text-slate-400 file:mr-3 file:bg-amber-500 file:text-slate-950 file:border-0 file:px-3 file:py-2 file:font-mono-brand file:text-xs file:cursor-pointer" />
        </div>
        <button type="submit" disabled={busy} data-testid="gallery-upload-button"
                className="sm:col-span-2 inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-condensed font-bold uppercase tracking-wide py-3 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {busy ? "Uploading…" : "Add To Gallery"}
        </button>
      </form>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {items.map((item) => (
          <div key={item.id} data-testid={`admin-gallery-item-${item.id}`} className="bg-[#0B0F17] border border-amber-500/15">
            <div className="grid grid-cols-2">
              <img src={resolveImage(item.before)} alt="before" className="w-full aspect-square object-cover" />
              <img src={resolveImage(item.after)} alt="after" className="w-full aspect-square object-cover" />
            </div>
            <div className="flex items-center justify-between p-3">
              <div className="min-w-0">
                <p className="text-sm font-condensed font-bold uppercase truncate">{item.title}</p>
                <p className="text-[10px] font-mono-brand uppercase tracking-[0.15em] text-amber-400">{item.category}</p>
              </div>
              <button onClick={() => remove(item.id)} data-testid={`gallery-delete-${item.id}`} aria-label="Delete"
                      className="text-slate-500 hover:text-rose-400 transition-colors shrink-0">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const DaysOffPanel = () => {
  const [days, setDays] = useState([]);
  const [date, setDate] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const today = new Date().toISOString().split("T")[0];

  const load = useCallback(() => {
    api.get("/blocked-days").then((r) => setDays(r.data)).catch(() => {});
  }, []);

  useEffect(load, [load]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/blocked-days", { date, reason });
      toast.success("Day blocked — customers can't book it");
      setDate(""); setReason("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/blocked-days/${id}`);
      setDays((ds) => ds.filter((d) => d.id !== id));
      toast.success("Day open for bookings again");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      <form onSubmit={add} data-testid="days-off-form" className="bg-[#0B0F17] border border-amber-500/15 p-6 mb-10 flex flex-col sm:flex-row gap-4 sm:items-end">
        <div>
          <label className="block text-xs font-mono-brand uppercase tracking-[0.2em] text-slate-500 mb-2">Day Off</label>
          <input required type="date" min={today} value={date} onChange={(e) => setDate(e.target.value)} data-testid="day-off-date-input"
                 className="bg-[#0F172A] border border-slate-700 focus:border-amber-500 outline-none px-4 py-3 text-sm text-slate-100 [color-scheme:dark]" />
        </div>
        <div className="flex-1">
          <label className="block text-xs font-mono-brand uppercase tracking-[0.2em] text-slate-500 mb-2">Reason (optional)</label>
          <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. Family day, RDO, holiday…" data-testid="day-off-reason-input"
                 className="w-full bg-[#0F172A] border border-slate-700 focus:border-amber-500 outline-none px-4 py-3 text-sm text-slate-100" />
        </div>
        <button type="submit" disabled={busy} data-testid="day-off-add-button"
                className="inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-condensed font-bold uppercase tracking-wide px-6 py-3 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarOff className="w-4 h-4" />}
          Block Day
        </button>
      </form>
      {days.length === 0 ? (
        <p className="text-slate-500 text-sm py-16 text-center" data-testid="days-off-empty">No days blocked — every day is open for bookings.</p>
      ) : (
        <div className="space-y-3">
          {days.map((d) => (
            <div key={d.id} data-testid={`day-off-row-${d.id}`} className="bg-[#0B0F17] border border-amber-500/15 p-4 flex items-center justify-between gap-4">
              <div>
                <p className="font-condensed font-bold uppercase tracking-wide text-slate-100">{d.date}</p>
                {d.reason && <p className="text-sm text-slate-500">{d.reason}</p>}
              </div>
              <button onClick={() => remove(d.id)} data-testid={`day-off-delete-${d.id}`} aria-label="Unblock day"
                      className="text-slate-500 hover:text-rose-400 transition-colors shrink-0">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const ReviewsPanel = () => {
  const [reviews, setReviews] = useState([]);

  const load = useCallback(() => {
    api.get("/admin/reviews").then((r) => setReviews(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(load, [load]);

  const setApproved = async (id, approved) => {
    try {
      const r = await api.patch(`/reviews/${id}`, { approved });
      setReviews((rs) => rs.map((x) => (x.id === id ? r.data : x)));
      toast.success(approved ? "Review live on the site" : "Review hidden");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/reviews/${id}`);
      setReviews((rs) => rs.filter((x) => x.id !== id));
      toast.success("Review deleted");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div>
      {reviews.length === 0 ? (
        <p className="text-slate-500 text-sm py-16 text-center" data-testid="reviews-empty">
          No reviews yet. They arrive when customers tap "Tell us how we did" in their job-done email.
        </p>
      ) : (
        <div className="space-y-4">
          {reviews.map((r) => (
            <div key={r.id} data-testid={`review-row-${r.id}`} className="bg-[#0B0F17] border border-amber-500/15 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-2">
                <div className="flex items-center gap-3">
                  <div className="flex gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star key={n} className={`w-4 h-4 ${n <= r.rating ? "text-amber-500 fill-amber-500" : "text-slate-700"}`} />
                    ))}
                  </div>
                  <span className="font-condensed font-bold uppercase tracking-wide text-slate-100">{r.name}</span>
                  <span className="text-xs font-mono-brand uppercase tracking-[0.15em] text-amber-400">{r.service_name}</span>
                </div>
                <span className={`text-[10px] font-mono-brand uppercase tracking-[0.15em] border px-2 py-0.5 ${r.approved ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" : "bg-amber-500/20 text-amber-400 border-amber-500/40"}`} data-testid={`review-status-${r.id}`}>
                  {r.approved ? "live on site" : "awaiting approval"}
                </span>
              </div>
              <p className="text-sm text-slate-300 mb-4">“{r.message}”</p>
              <div className="flex gap-2">
                <button onClick={() => setApproved(r.id, !r.approved)} data-testid={`review-approve-${r.id}`}
                        className="text-xs font-mono-brand uppercase tracking-[0.1em] border border-slate-700 hover:border-amber-500/60 text-slate-300 hover:text-amber-400 px-3 py-2 transition-colors">
                  {r.approved ? "Hide From Site" : "Approve & Publish"}
                </button>
                <button onClick={() => remove(r.id)} data-testid={`review-delete-${r.id}`}
                        className="text-xs font-mono-brand uppercase tracking-[0.1em] border border-slate-700 hover:border-rose-500/60 text-slate-400 hover:text-rose-400 px-3 py-2 transition-colors">
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");

  if (loading) {
    return <div className="min-h-screen bg-[#0B0F17] flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>;
  }
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-[#0B0F17] text-slate-100" data-testid="admin-dashboard">
      <header className="border-b border-amber-500/15 bg-[#0F172A]">
        <div className="px-4 sm:px-8 max-w-6xl mx-auto h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 bg-amber-500 flex items-center justify-center">
              <Zap className="w-5 h-5 text-slate-950" strokeWidth={2.5} />
            </span>
            <span className="font-condensed font-extrabold uppercase tracking-wide">Owner Dashboard</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/fix-and-finish-qr.png" target="_blank" rel="noopener noreferrer" data-testid="admin-qr-link"
               className="inline-flex items-center gap-2 text-xs font-mono-brand uppercase tracking-[0.15em] text-slate-400 hover:text-amber-400 transition-colors">
              <QrCode className="w-4 h-4" /> My QR Code
            </a>
            <button onClick={() => { logout(); navigate("/"); }} data-testid="admin-logout-button"
                  className="inline-flex items-center gap-2 text-xs font-mono-brand uppercase tracking-[0.15em] text-slate-400 hover:text-rose-400 transition-colors">
            <LogOut className="w-4 h-4" /> Logout
          </button>
          </div>
        </div>
      </header>
      <main className="px-4 sm:px-8 max-w-6xl mx-auto py-10">
        <div className="flex gap-2 mb-8">
          {[["bookings", "Bookings"], ["gallery", "Gallery"], ["daysoff", "Days Off"], ["reviews", "Reviews"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
                    className={`font-condensed font-bold uppercase tracking-wide px-6 py-2.5 border transition-colors ${tab === key ? "bg-amber-500 text-slate-950 border-amber-500" : "text-slate-400 border-slate-700 hover:border-amber-500/50"}`}>
              {label}
            </button>
          ))}
        </div>
        {tab === "bookings" ? <BookingsPanel /> : tab === "gallery" ? <GalleryPanel /> : tab === "daysoff" ? <DaysOffPanel /> : <ReviewsPanel />}
      </main>
    </div>
  );
}
